import type { BackendCrop } from '../../api/crop';
import { useCrops } from '../../hooks/useCrops';

/**
 * Crop picker backed by the admin-curated "Cây trồng" catalog, replacing the
 * free-text crop inputs: a typed "Chuoi" / "CHuoi" / "Chuối" was three
 * different crops to every report and match. The value stays the crop's name
 * (what records store as mainCrop/cayTrong); `onChange` also hands back the
 * Crop so callers that link records to it (season.cropId) can. A value from
 * before this picker that is not in the catalog is still shown, marked as such,
 * so editing an old record never silently blanks it.
 */
export function CropSelect({
  value,
  onChange,
  htxId,
  className,
  invalid,
  placeholder = '— Chọn cây trồng —',
}: {
  value: string;
  onChange: (name: string, crop?: BackendCrop) => void;
  htxId?: string;
  className?: string;
  invalid?: boolean;
  placeholder?: string;
}) {
  const crops = useCrops(htxId);
  const inCatalog = !value || !!crops?.some((c) => c.name === value);

  return (
    <div>
      <select
        value={value}
        onChange={(e) => {
          const crop = crops?.find((c) => c.name === e.target.value);
          onChange(e.target.value, crop);
        }}
        disabled={crops === null}
        aria-invalid={invalid}
        className={className ?? `w-full rounded-lg border px-3 py-2 text-sm bg-white ${invalid ? 'border-red-400' : 'border-gray-300'}`}
      >
        <option value="">{crops === null ? 'Đang tải danh mục cây trồng...' : placeholder}</option>
        {crops?.map((c) => (
          <option key={c._id} value={c.name}>
            {c.name}
            {c.scope === 'HTX' ? ' (riêng HTX)' : ''}
          </option>
        ))}
        {!inCatalog && <option value={value}>{value} (không có trong danh mục)</option>}
      </select>
      {crops !== null && crops.length === 0 && (
        <p className="mt-1 text-[11px] text-amber-600">Chưa có cây trồng nào trong danh mục — nhờ Agrida thêm ở trang Cây trồng (admin).</p>
      )}
    </div>
  );
}
